// ============================================================
// مجموعه‌داده‌ها — لایه‌ای که دستیار را از «چت‌بات» به «دستیار داده» می‌کند
//
// هر مجموعه‌داده سه چیز را می‌داند:
//   ۱) چه نقش‌هایی حق دیدنش را دارند (همان rolesAllowed مسیرها)
//   ۲) چه ورودی‌هایی لازم دارد (slots) — دستیار همین‌ها را می‌پرسد
//   ۳) چطور از API گرفته و چه ستون‌هایی نشان داده شود
//
// چرا slot: کاربر می‌گوید «اطلاعات خبازی». این جمله ناقص است. دستیار
// نباید حدس بزند و نباید هزار رکورد بریزد؛ باید بپرسد «نام خبازی؟».
// ============================================================

import Api from "Common/Components/ApiInterceptor/Api";
import { DateRange, parseDateRange, todayJalali } from "./dates";
import {
    BakerQuota,
    QUOTA_UNIT,
    describeBaker,
    lookupBaker,
    monthNameIn,
    monthOf,
} from "./quota";
import { normalize, toEnglishDigits, toPersianDigits } from "./text";

// ---------- انواع ----------

export type SlotKind = "text" | "daterange" | "choice";

export interface ChoiceOption {
    value: string;
    label: string;
}

export interface Slot {
    key: string;
    label: string;
    kind: SlotKind;
    /** سوالی که دستیار می‌پرسد */
    prompt: string;
    hint?: string;
    /** با «همه» یا «مهم نیست» می‌توان ردش کرد */
    optional?: boolean;
    defaultValue?: string;
    /**
     * فقط برای kind === "choice".
     * مقادیر اسلات‌های پرشده را می‌گیرد، چون مثلاً «مرحله توزیع» به
     * «دوره توزیع» وابسته است و بدون آن فهرست خالی برمی‌گردد.
     */
    options?: (values: Record<string, string>) => Promise<ChoiceOption[]>;
}

export interface Column {
    key: string;
    label: string;
    format?: (value: any, row: any) => string;
}

export interface QueryResult {
    title: string;
    columns: Column[];
    rows: any[];
    /** تعداد کل رکوردهای موجود در سرور (نه فقط همین صفحه) */
    total: number;
    /** خلاصه‌ی خوانا — همین متن با صدا خوانده می‌شود */
    spoken: string;
    note?: string;
    link?: string;
    detail?: Array<{ label: string; value: string }>;
}

export interface Dataset {
    id: string;
    title: string;
    roles: number[];
    link: string;
    /**
     * گروه‌های allOf: هر گروه باید دست‌کم یک عضوش در جمله باشد، ولی
     * لازم نیست کنار هم باشند. در فارسی ذرات وسط عبارت می‌آیند
     * («خبازی رو بهم بده») و تطبیق عبارتی اینجا شکست می‌خورد.
     */
    match: string[][];
    /** واژه‌هایی که برای بیرون کشیدن مقدار اسلات از جمله حذف می‌شوند */
    strip: string[];
    slots: Slot[];
    run: (values: Record<string, string>) => Promise<QueryResult>;
    /**
     * صفحه‌ی متناظر کادر جست‌وجو دارد، پس می‌شود فیلتر را به خودش
     * تحویل داد (`lib/pageFilter.ts`). گزارش‌ها این را ندارند.
     */
    filterable?: boolean;
    /** صفحه‌ی متناظر تب «انجام‌شده / انجام‌نشده» دارد */
    hasStatusTab?: boolean;
    /**
     * فیلد تاریخ شمسی که فیلتر بازه روی آن اعمال می‌شود.
     * تابع است چون در وظایف، فیلد معنادار به وضعیت بستگی دارد:
     * رکورد انجام‌نشده هنوز تاریخ تخلیه ندارد.
     */
    dateField?: (values: Record<string, string>) => string;
    /** گرفتن همه‌ی رکوردها برای خروجی اکسل */
    fetchAll?: (values: Record<string, string>) => Promise<any[]>;
}

// ---------- کمک‌ها ----------

const PAGE_SIZE = 20;

const dash = (v: any): string =>
    v === null || v === undefined || v === "" ? "—" : String(v);

const num = (v: any): string =>
    v === null || v === undefined || v === "" ? "—" : Number(v).toLocaleString("fa-IR");

const BAKERY_TYPE_LABELS: Record<string, string> = { urban: "شهری", rural: "روستایی" };

const STATUS_OPTIONS: ChoiceOption[] = [
    { value: "not_done", label: "انجام‌نشده" },
    { value: "done", label: "انجام‌شده" },
];

// وضعیت پیش‌فرض «انجام‌نشده» است و پرسیده نمی‌شود؛ اگر کاربر در جمله
// «انجام‌شده» گفته باشد، خودش تشخیص داده می‌شود.
const statusSlot = (): Slot => ({
    key: "status",
    label: "وضعیت",
    kind: "choice",
    prompt: "«انجام‌نشده» یا «انجام‌شده»؟",
    defaultValue: "not_done",
    options: async () => STATUS_OPTIONS,
});

/**
 * اسلات جست‌وجوی اختیاری.
 * اختیاری است یعنی دستیار آن را نمی‌پرسد؛ اگر کاربر در جمله‌اش نامی
 * داده باشد اعمال می‌شود، وگرنه آخرین رکوردها نشان داده می‌شوند.
 */
const searchSlot = (prompt: string): Slot => ({
    key: "search",
    label: "عبارت جست‌وجو",
    kind: "text",
    prompt,
    hint: "برای دیدن همه بگویید «همه»",
    optional: true,
});

const dateRangeSlot: Slot = {
    key: "range",
    label: "بازه تاریخ",
    kind: "daterange",
    prompt: "برای چه تاریخی؟ می‌توانید بگویید «امروز»، «دیروز»، «۱۴۰۴/۰۳/۱۲» یا «از ۱۴۰۴/۰۳/۰۱ تا ۱۴۰۴/۰۳/۱۰».",
    hint: "مثال: دیروز / این هفته / ۱۴۰۴/۰۳/۱۲",
};

/** خواندن بازه از مقدار ذخیره‌شده‌ی اسلات (که به شکل «start|end|label» است). */
export const packRange = (r: DateRange): string => r.start + "|" + r.end + "|" + r.label;
export const unpackRange = (value: string): DateRange => {
    const parts = String(value || "").split("|");
    return {
        start: parts[0] || todayJalali(),
        end: parts[1] || parts[0] || todayJalali(),
        label: parts[2] || parts[0] || "",
    };
};

interface ListResponse<T> {
    total: number;
    size: number;
    pages: number;
    items: T[];
}

const getList = async <T>(url: string, params: Record<string, any>) => {
    const res = await Api.get<ListResponse<T>>(url, { params });
    return {
        items: res.data.items || [],
        total: res.data.total || 0,
        pages: res.data.pages || 0,
    };
};

/** سقف صفحاتی که برای فیلتر تاریخ سمت کلاینت اسکن می‌شود. */
const MAX_SCAN_PAGES = 10;
/** سقف صفحاتی که برای خروجی اکسل گرفته می‌شود. */
const MAX_EXPORT_PAGES = 100;
const API_PAGE_SIZE = 100;

export const withinRange = (value: any, range: DateRange): boolean => {
    const date = toEnglishDigits(value || "").slice(0, 10);
    if (!date) return false;
    return date >= range.start && date <= range.end;
};

/** همه‌ی صفحه‌ها را پشت سر هم می‌گیرد (با سقف) و اختیاراً فیلتر می‌کند. */
/**
 * چند درخواست همزمان.
 *
 * نسخه‌ی قبلی صفحه‌ها را یکی‌یکی می‌گرفت: برای یک فیلتر تاریخ، ۱۰
 * رفت‌وبرگشت *پشت سر هم* — روی API کند یعنی چند ثانیه انتظار. حالا
 * صفحه‌ی اول می‌آید تا تعداد کل معلوم شود، بعد بقیه دسته‌دسته و
 * همزمان. همان کاری که صفحه‌ی اطلاعات خبازی‌ها هم می‌کند.
 *
 * چرا ۵ و نه همه با هم: صد درخواست همزمان، هم سرور را می‌خواباند هم
 * سقف اتصال مرورگر را پر می‌کند.
 */
const SCAN_CONCURRENCY = 5;

const scanPages = async (
    url: string,
    params: Record<string, any>,
    maxPages: number,
    keep?: (row: any) => boolean
): Promise<any[]> => {
    const collected: any[] = [];
    const take = (items: any[]) => {
        for (let i = 0; i < items.length; i++) {
            const row = items[i];
            if (!keep || keep(row)) collected.push(row);
        }
    };

    const first = await getList<any>(url, { ...params, page: 1, size: API_PAGE_SIZE });
    take(first.items);

    const lastPage = Math.min(first.pages || 1, maxPages);
    const remaining: number[] = [];
    for (let page = 2; page <= lastPage; page++) remaining.push(page);

    // دسته‌ها ترتیبی‌اند و Promise.all ترتیب داخل دسته را نگه می‌دارد،
    // پس ترتیب صفحه‌ها به هم نمی‌ریزد.
    for (let i = 0; i < remaining.length; i += SCAN_CONCURRENCY) {
        const batch = remaining.slice(i, i + SCAN_CONCURRENCY);
        const responses = await Promise.all(
            batch.map((page) => getList<any>(url, { ...params, page, size: API_PAGE_SIZE }))
        );
        responses.forEach((response) => take(response.items));
    }

    return collected;
};

interface ListQuery {
    rows: any[];
    total: number;
    note?: string;
}

/**
 * تعداد سطر مؤثر: «۵۰ سطر نشون بده» → ۵۰، وگرنه پیش‌فرض.
 * سقف دارد تا یک اشتباه تایپی، هزار رکورد را روی سر مرورگر خالی نکند.
 */
export const sizeOf = (values: Record<string, string>): number => {
    const requested = Number(values.size);
    return requested && requested > 0 ? Math.min(requested, 500) : PAGE_SIZE;
};

/**
 * یک صفحه داده می‌گیرد — مگر اینکه بازه‌ی تاریخ داده شده باشد.
 *
 * هیچ‌کدام از مسیرهای فهرست، پارامتر بازه‌ی تاریخ ندارند. پس وقتی
 * کاربر تاریخ می‌دهد، چند صفحه گرفته و سمت کلاینت فیلتر می‌شود.
 * نتیجه درست است، فقط محدود به رکوردهای اسکن‌شده — و همین محدودیت
 * در note به کاربر گفته می‌شود.
 */
const listQuery = async (
    url: string,
    params: Record<string, any>,
    range?: DateRange,
    dateField?: string,
    size: number = PAGE_SIZE
): Promise<ListQuery> => {
    if (!range || !dateField) {
        const found = await getList<any>(url, { ...params, page: 1, size });
        return { rows: found.items, total: found.total };
    }

    const matched = await scanPages(url, params, MAX_SCAN_PAGES, (row) =>
        withinRange(row[dateField], range)
    );

    return {
        rows: matched.slice(0, size),
        total: matched.length,
        note:
            "فیلتر تاریخ (" + range.label + ") روی حداکثر " +
            MAX_SCAN_PAGES * API_PAGE_SIZE +
            " رکورد آخر اعمال شد؛ این مسیر در سرور پارامتر تاریخ ندارد.",
    };
};

/** اسلات بازه‌ی تاریخ، اختیاری: پرسیده نمی‌شود، فقط اگر در جمله بود اعمال می‌شود. */
const optionalRangeSlot: Slot = {
    key: "range",
    label: "بازه تاریخ",
    kind: "daterange",
    prompt: "برای چه تاریخی؟",
    optional: true,
};

/**
 * واژه‌های وضعیت — باید از عبارت جست‌وجو حذف شوند.
 *
 * صورت‌های «…های» هم لازم‌اند و این یک باگ واقعی بود: در «تب ثبت
 * نشده‌های وظایف راننده»، الگوی «ثبت نشده ها» زیررشته‌ی «ثبت نشده
 * های» است، پس حذف می‌شد و یک «ی» یتیم ته جمله می‌ماند که به عنوان
 * عبارت جست‌وجو به سرور می‌رفت.
 */
const STATUS_WORDS = [
    "ثبت نشده های", "ثبت نشده ها", "ثبت نشده", "ثبت شده های", "ثبت شده ها", "ثبت شده",
    "انجام نشده های", "انجام نشده ها", "انجام نشده",
    "انجام شده های", "انجام شده ها", "انجام شده",
    "نشده های", "نشده ها", "شده های", "شده ها",
];

/** خلاصه‌ی یک نتیجه‌ی فهرستی، برای متن پاسخ و خواندن با صدا. */
const listSummary = (title: string, shown: number, total: number, filter?: string): string => {
    if (total === 0) {
        return "برای " + title + (filter ? " با «" + filter + "»" : "") + " رکوردی پیدا نشد.";
    }
    const head =
        total + " رکورد برای " + title + (filter ? " با «" + filter + "»" : "") + " پیدا شد";
    return shown < total ? head + "؛ " + shown + " مورد اول را نشان می‌دهم." : head + ".";
};

// ---------- ستون‌ها ----------

const BAKERY_COLUMNS: Column[] = [
    { key: "baker_name", label: "نام خبازی", format: dash },
    { key: "baker_code", label: "کد خبازی", format: dash },
    { key: "city", label: "شهر", format: dash },
    { key: "province", label: "استان", format: dash },
    { key: "bakery_type", label: "نوع", format: (v) => BAKERY_TYPE_LABELS[v] || dash(v) },
    { key: "day_off", label: "روز تعطیل", format: dash },
    { key: "address", label: "آدرس", format: dash },
];

const ORDER_COLUMNS: Column[] = [
    { key: "order_code", label: "کد حواله", format: dash },
    { key: "baker_name", label: "نانوا", format: dash },
    { key: "city", label: "شهر", format: dash },
    { key: "distribution_period", label: "دوره", format: dash },
    { key: "distribution_stage", label: "مرحله توزیع", format: dash },
    { key: "voucher_bag_count", label: "کیسه حواله", format: num },
    { key: "driver_discharge_count", label: "تخلیه راننده", format: num },
    { key: "inspector_counted_count", label: "شمارش بازرس", format: num },
    { key: "voucher_date_jalali", label: "تاریخ حواله", format: dash },
    { key: "discharge_date_jalali", label: "تاریخ تخلیه", format: dash },
];

/** ستون‌های پاسخ ماه‌به‌ماه: همان چیزی که با صدا هم گفته می‌شود. */
const QUOTA_MONTH_COLUMNS: Column[] = [
    { key: "label", label: "دوره", format: dash },
    { key: "quota", label: "سهمیه (" + QUOTA_UNIT + ")", format: (v) => Number(v || 0).toFixed(1) },
    {
        key: "delta",
        label: "تغییر نسبت به ماه قبل",
        format: (v) => {
            if (v === undefined || v === null) return "—";
            const value = Number(v);
            if (value === 0) return "بدون تغییر";
            return (value > 0 ? "+" : "−") + Math.abs(value).toFixed(1);
        },
    },
];

/**
 * پاسخ گفتگویی سهمیه‌ی یک خبازی.
 *
 * سه حالت دارد و هر سه عمدی‌اند: پیدا شد، چند نفر با این نام، و
 * پیدا نشد. حالت سوم مهم‌ترین است — در ورودی صوتی، اشتباه شنیدن
 * نام قاعده است نه استثنا، پس به‌جای «نتیجه‌ای نبود» نزدیک‌ترین
 * نام‌ها پیشنهاد می‌شوند.
 */
const describeQuotaFor = async (search: string, sentence: string): Promise<QueryResult> => {
    const lookup = await lookupBaker(search);

    if (lookup.kind === "none") {
        const names = lookup.suggestions || [];
        const text = names.length
            ? "خبازی‌ای با نام «" + search + "» پیدا نکردم. منظورتان یکی از این‌ها بود؟ " +
              names.join("، ")
            : "خبازی‌ای با نام «" + search + "» پیدا نکردم. نام را دوباره بگویید یا کد خریدار را بدهید.";

        return {
            title: "سهمیه خبازی‌ها",
            columns: QUOTA_MONTH_COLUMNS,
            rows: [],
            total: 0,
            link: "/BakerQuota",
            spoken: text,
        };
    }

    if (lookup.kind === "many") {
        const matches = lookup.matches || [];
        const text =
            toPersianDigits(matches.length) + " خبازی با نام «" + search +
            "» هست. کدام را می‌خواهید؟ " +
            matches.map((b) => b.name + " از " + (b.city || "نامشخص")).join("، ");

        return {
            title: "چند خبازی با این نام",
            columns: [
                { key: "name", label: "نام", format: dash },
                { key: "code", label: "کد خریدار", format: dash },
                { key: "city", label: "شهرستان", format: dash },
                { key: "bakingType", label: "نوع پخت", format: dash },
            ],
            rows: matches,
            total: matches.length,
            link: "/BakerQuota",
            spoken: text,
        };
    }

    const baker = lookup.baker as BakerQuota;

    // کاربر ماه خاصی را نام برده؟ آن ماه جلو می‌افتد، بقیه هم می‌مانند.
    const askedMonth = monthNameIn(sentence);
    const one = askedMonth ? monthOf(baker, askedMonth) : null;

    const spoken = one
        ? "سهمیه‌ی " + baker.name + " در " + one.label + "، " +
          toPersianDigits(one.quota.toFixed(1)) + " " + QUOTA_UNIT + " است." +
          (one.delta === undefined
              ? ""
              : one.delta === 0
              ? " نسبت به ماه قبل تغییری نکرده."
              : " نسبت به ماه قبل " + toPersianDigits(Math.abs(one.delta).toFixed(1)) + " " +
                QUOTA_UNIT + (one.delta > 0 ? " بیشتر" : " کمتر") + " شده.") +
          "\n\n" + describeBaker(baker)
        : describeBaker(baker);

    return {
        title: "سهمیه " + baker.name + (baker.city ? " — " + baker.city : ""),
        columns: QUOTA_MONTH_COLUMNS,
        rows: baker.months,
        total: baker.months.length,
        link: "/BakerQuota",
        detail: [
            { label: "نام", value: baker.name },
            { label: "کد خریدار", value: baker.code || "—" },
            { label: "شهرستان", value: baker.city || "—" },
            { label: "نوع پخت", value: baker.bakingType || "—" },
            { label: "تعداد دوره", value: toPersianDigits(baker.months.length) },
        ],
        spoken,
    };
};

const QUOTA_COLUMNS: Column[] = [
    { key: "buyer_name", label: "نام خریدار", format: dash },
    { key: "buyer_code", label: "کد خریدار", format: dash },
    { key: "city", label: "شهر", format: dash },
    { key: "baking_type", label: "نوع پخت", format: dash },
    { key: "distribution_period", label: "دوره توزیع", format: dash },
    { key: "quota", label: "سهمیه", format: num },
];

const VIOLATION_COLUMNS: Column[] = [
    { key: "baker_name", label: "نانوا", format: dash },
    { key: "city", label: "شهر", format: dash },
    { key: "status", label: "وضعیت", format: dash },
    { key: "quota", label: "سهمیه", format: num },
    { key: "unloaded_count", label: "تخلیه‌شده", format: num },
    { key: "inspector_counted_count", label: "شمارش بازرس", format: num },
    { key: "out_of_network", label: "خارج از شبکه", format: num },
    { key: "under_baking", label: "کم‌پزی", format: num },
    { key: "discharge_date_jalali", label: "تاریخ تخلیه", format: dash },
];

const REPORT_COLUMNS: Column[] = [
    { key: "label", label: "عنوان", format: dash },
    { key: "voucher_bag_count", label: "حواله نانوا", format: num },
    { key: "driver_discharge_bag_count", label: "تخلیه راننده", format: num },
    { key: "inspector_registration_bag_count", label: "ثبت بازرس", format: num },
    { key: "driver_not_discharged_bag_count", label: "عدم تخلیه راننده", format: num },
    { key: "inspector_not_registered_bag_count", label: "عدم ثبت بازرس", format: num },
];

const USER_COLUMNS: Column[] = [
    { key: "fullname", label: "نام و نام خانوادگی", format: dash },
    { key: "username", label: "نام کاربری", format: dash },
    { key: "phone_number", label: "تلفن", format: dash },
    { key: "role", label: "نقش", format: dash },
    { key: "disabled", label: "وضعیت", format: (v) => (v ? "غیرفعال" : "فعال") },
];

const DRIVER_TASK_COLUMNS: Column[] = [
    { key: "baker_name", label: "نانوا", format: dash },
    { key: "baker_code", label: "کد خبازی", format: dash },
    { key: "city", label: "شهر", format: dash },
    { key: "driver_name", label: "راننده", format: dash },
    { key: "driver_available_count", label: "کیسه قابل تخلیه", format: num },
    { key: "driver_discharge_count", label: "تخلیه‌شده", format: num },
    { key: "discharge_date_jalali", label: "تاریخ تخلیه", format: dash },
];

const INSPECTOR_TASK_COLUMNS: Column[] = [
    { key: "baker_name", label: "نانوا", format: dash },
    { key: "baker_code", label: "کد خبازی", format: dash },
    { key: "city", label: "شهر", format: dash },
    { key: "driver_name", label: "راننده", format: dash },
    { key: "inspector_counted_count", label: "شمارش بازرس", format: num },
    { key: "discharge_date_jalali", label: "تاریخ تخلیه", format: dash },
    { key: "inspection_date_jalali", label: "تاریخ بازرسی", format: dash },
];

const AI_TASK_COLUMNS: Column[] = [
    { key: "baker_name", label: "نانوا", format: dash },
    { key: "baker_phone", label: "تلفن نانوا", format: dash },
    { key: "auto_counted_count", label: "شمارش خودکار", format: num },
    { key: "created_at_jalali", label: "تاریخ ثبت", format: dash },
];

// ---------- دراپ‌داون‌ها (با کش، چون در یک نشست عوض نمی‌شوند) ----------

const dropdownCache: Record<string, ChoiceOption[]> = {};

const loadDropdown = async (
    url: string,
    valueKey: string,
    labelKey: string,
    params?: Record<string, any>
): Promise<ChoiceOption[]> => {
    const cacheKey = url + "?" + JSON.stringify(params || {});
    if (dropdownCache[cacheKey]) return dropdownCache[cacheKey];
    const res = await Api.get<any>(url, { params });
    const raw = Array.isArray(res.data) ? res.data : res.data?.items || [];
    const options = raw.map((item: any) => ({
        value: String(item[valueKey]),
        label: String(item[labelKey] ?? item[valueKey]),
    }));
    dropdownCache[cacheKey] = options;
    return options;
};

// ============================================================
// تعریف مجموعه‌داده‌ها
// ============================================================

export const DATASETS: Dataset[] = [
    // ---------- اطلاعات خبازی‌ها ----------
    {
        id: "bakeries",
        title: "اطلاعات خبازی‌ها",
        roles: [0, 1, 2, 5, 6],
        link: "/BakeryInformation",
        filterable: true,
        // تاریخ خبازی‌ها = تاریخ *ثبت* رکورد. «اطلاعات خبازی تاریخ
        // امروز» یعنی خبازی‌هایی که امروز ثبت شده‌اند، نه همه‌ی
        // خبازی‌ها. این را در note به کاربر می‌گوییم.
        dateField: () => "created_at_jalali",
        fetchAll: (values) =>
            scanPages(
                "/monitoring/bakeries/",
                values.search ? { search: values.search } : {},
                MAX_EXPORT_PAGES
            ),
        match: [
            ["خبازی", "خبازی ها", "نانوایی", "نانوا", "خباز"],
            ["اطلاعات", "مشخصات", "پروفایل", "جزییات", "جزئیات", "آدرس", "ادرس", "موقعیت", "دیتا", "داده", "رکورد", "سابقه"],
        ],
        strip: [
            "اطلاعات", "مشخصات", "پروفایل", "جزییات", "جزئیات", "خبازی ها", "خبازی",
            "نانوایی", "نانوا", "خباز", "دیتا", "داده", "رکورد", "بده", "بدم", "بهم",
            "نشونم", "نشون", "بگو", "میخوام", "می خوام", "رو", "را", "لطفا", "هاشو", "هاش", "شو",
        ],
        slots: [
            // اختیاری است، پس پرسیده نمی‌شود: «اطلاعات خبازی نشونم بده»
            // باید کل فهرست را بیاورد، نه اینکه اول نام بپرسد.
            searchSlot("نام و نام خانوادگی خبازی یا کد خبازی را بگویید. برای دیدن همه بگویید «همه»."),
            optionalRangeSlot,
        ],
        run: async (values) => {
            const search = String(values.search || "").trim();
            const range = values.range ? unpackRange(values.range) : undefined;
            const size = sizeOf(values);

            let found = await listQuery(
                "/monitoring/bakeries/",
                search ? { search } : {},
                range,
                "created_at_jalali",
                size
            );

            // اگر جست‌وجوی سمت سرور چیزی نداد، چند صفحه را می‌گیریم و
            // خودمان می‌گردیم. علتش این است که این مسیر گاهی روی نام
            // با فاصله‌ی چندگانه یا نیم‌فاصله چیزی برنمی‌گرداند.
            if (search && found.total === 0) {
                const needle = normalize(search);
                const collected = await scanPages("/monitoring/bakeries/", {}, 5, (b) => {
                    const haystack = normalize(
                        [b.baker_name, b.baker_code, b.city, b.province, b.address]
                            .filter(Boolean)
                            .join(" ")
                    );
                    return haystack.indexOf(needle) !== -1;
                });
                found = { rows: collected.slice(0, size), total: collected.length };
            }

            const rows = found.rows;
            const result: QueryResult = {
                title: "اطلاعات خبازی‌ها" + (range ? " — ثبت‌شده در " + range.label : ""),
                columns: BAKERY_COLUMNS,
                rows,
                total: found.total,
                link: "/BakeryInformation",
                note: range
                    ? "تاریخ روی «تاریخ ثبت رکورد» اعمال شد. " + (found.note || "")
                    : found.note,
                spoken: listSummary("اطلاعات خبازی‌ها", rows.length, found.total, search),
            };

            // یک نتیجه‌ی دقیق → کارت جزئیات کامل
            if (rows.length === 1) {
                const b = rows[0];
                result.detail = [
                    { label: "نام خبازی", value: dash(b.baker_name) },
                    { label: "کد خبازی", value: dash(b.baker_code) },
                    { label: "استان / شهر", value: dash(b.province) + " / " + dash(b.city) },
                    { label: "نوع خبازی", value: BAKERY_TYPE_LABELS[b.bakery_type] || dash(b.bakery_type) },
                    { label: "روز تعطیل", value: dash(b.day_off) },
                    { label: "آدرس", value: dash(b.address) },
                    {
                        label: "موقعیت",
                        value:
                            b.location_lat && b.location_lng
                                ? dash(b.location_lat) + " , " + dash(b.location_lng)
                                : "ثبت نشده",
                    },
                    { label: "آخرین به‌روزرسانی", value: dash(b.updated_at_jalali) },
                ];
                // متن خوانده‌شده باید همان چیزی باشد که روی کارت است.
                // قبلاً فقط نام و کد و شهر خوانده می‌شد و کاربری که
                // جواب را با گوش می‌گرفت، آدرس و نوع خبازی را از دست
                // می‌داد — یعنی دقیقاً چیزهایی که پرسیده بود.
                const parts: string[] = [
                    "خبازی " + dash(b.baker_name) + "، کد " + dash(b.baker_code),
                ];

                const place = [b.province, b.city].filter(Boolean).join("، ");
                if (place) parts.push("در " + place);

                const type = BAKERY_TYPE_LABELS[b.bakery_type] || b.bakery_type;
                if (type) parts.push("نوع خبازی: " + type);
                if (b.day_off) parts.push("روز تعطیل: " + b.day_off);
                if (b.address) parts.push("آدرس: " + b.address);

                parts.push(
                    b.location_lat && b.location_lng
                        ? "موقعیت مکانی‌اش روی نقشه ثبت شده است"
                        : "موقعیت مکانی‌اش ثبت نشده است"
                );

                result.spoken = parts.join("، ") + ".";
            }

            return result;
        },
    },

    // ---------- حواله‌های نانوا ----------
    {
        id: "orders",
        title: "حواله‌های نانوا",
        roles: [0, 1, 3, 5, 6, 7],
        link: "/ManageOrders",
        match: [
            ["حواله", "حواله ها", "سفارش", "سفارشات", "سفارش ها"],
            ["نشون", "بده", "بدم", "لیست", "اطلاعات", "دیتا", "رکورد", "ببین", "چند", "چقدر", "وضعیت", "بگو"],
        ],
        strip: [
            "حواله های نانوا", "حواله ها", "حواله", "سفارشات", "سفارش ها", "سفارش",
            "لیست", "اطلاعات", "دیتا", "رکورد", "نشونم", "نشون", "بده", "بدم", "بهم", "رو", "را",
        ],
        filterable: true,
        dateField: () => "voucher_date_jalali",
        slots: [
            searchSlot("کد حواله، نام نانوا یا نام راننده را بگویید. اگر آخرین حواله‌ها را می‌خواهید بگویید «همه»."),
            optionalRangeSlot,
        ],
        fetchAll: (values) =>
            scanPages(
                "/monitoring/orders",
                {
                    sort_by: "id",
                    sort_order: "desc",
                    ...(values.search ? { search: values.search } : {}),
                },
                MAX_EXPORT_PAGES
            ),
        run: async (values) => {
            const search = String(values.search || "").trim();
            const range = values.range ? unpackRange(values.range) : undefined;
            const params: Record<string, any> = { sort_by: "id", sort_order: "desc" };
            if (search) params.search = search.replace(/\s+/g, " ");

            const found = await listQuery(
                "/monitoring/orders",
                params,
                range,
                "voucher_date_jalali",
                sizeOf(values)
            );
            return {
                title: "حواله‌های نانوا" + (range ? " — " + range.label : ""),
                columns: ORDER_COLUMNS,
                rows: found.rows,
                total: found.total,
                link: "/ManageOrders",
                note: found.note,
                spoken: listSummary("حواله‌های نانوا", found.rows.length, found.total, search),
            };
        },
    },

    // ---------- گزارش توزیع آرد (بر اساس تاریخ) ----------
    {
        id: "reports",
        title: "گزارش توزیع آرد",
        roles: [0, 1, 3, 5, 6, 7],
        link: "/reports",
        match: [
            ["گزارش", "گزارش ها", "گزارشات", "آمار", "امار"],
            [
                "تاریخ", "امروز", "دیروز", "پریروز", "هفته", "ماه", "روزانه", "بده", "بدم",
                "بگیر", "بیار", "نشون", "توزیع", "آرد", "ارد", "1", "2", "3", "4", "5", "6", "7", "8", "9",
            ],
        ],
        strip: ["گزارش ها", "گزارشات", "گزارش", "آمار", "امار", "توزیع آرد", "توزیع", "بده", "بدم", "بگیر", "بیار", "رو", "را", "برای", "تاریخ"],
        // اختیاری است و پرسیده نمی‌شود: «گزارش ها رو نشون بده» باید
        // مثل خود صفحه‌ی گزارش‌ها، پیش‌فرض امروز را بیاورد.
        slots: [{ ...dateRangeSlot, optional: true }],
        filterable: true,
        run: async (values) => {
            const range = values.range
                ? unpackRange(values.range)
                : { start: todayJalali(), end: todayJalali(), label: "امروز" };
            const voucherDays = Number(values.voucher_days) || 30;
            const inspectionDays = Number(values.inspection_days) || 30;

            const res = await Api.get<any>("/monitoring/reports/flour-distribution", {
                params: {
                    start_date: range.start,
                    end_date: range.end,
                    voucher_to_discharge_days: voucherDays,
                    discharge_to_inspection_days: inspectionDays,
                },
            });
            const rows = res.data?.rows || [];
            const totals = rows.reduce(
                (acc: any, r: any) => {
                    acc.voucher += Number(r.voucher_bag_count || 0);
                    acc.discharge += Number(r.driver_discharge_bag_count || 0);
                    acc.inspection += Number(r.inspector_registration_bag_count || 0);
                    return acc;
                },
                { voucher: 0, discharge: 0, inspection: 0 }
            );

            return {
                title: "گزارش توزیع آرد — " + range.label,
                columns: REPORT_COLUMNS,
                rows,
                total: rows.length,
                link: "/reports",
                note:
                    "بازه: " + range.start + " تا " + range.end +
                    " · حواله تا تخلیه: " + voucherDays +
                    " روز · تخلیه تا بازرسی: " + inspectionDays + " روز",
                spoken:
                    rows.length === 0
                        ? "برای بازه " + range.label + " گزارشی ثبت نشده است."
                        : "گزارش " + range.label + ": " + rows.length + " ردیف. مجموع حواله " +
                          totals.voucher + " کیسه، تخلیه راننده " + totals.discharge +
                          " کیسه و ثبت بازرس " + totals.inspection + " کیسه.",
            };
        },
    },

    // ---------- گزارش دهه (دوره/مرحله) ----------
    {
        id: "reports-decade",
        title: "گزارش دهه",
        roles: [0, 1, 3, 5, 6, 7],
        link: "/ReportsDecadePage",
        match: [
            ["گزارش دهه", "دهه", "دوره توزیع", "مرحله توزیع"],
            ["گزارش", "آمار", "امار", "بده", "بدم", "نشون", "بیار", "دهه", "دوره", "مرحله"],
        ],
        strip: ["گزارش دهه", "گزارش", "دهه", "دوره توزیع", "مرحله توزیع", "بده", "بدم", "رو", "را"],
        slots: [
            {
                key: "distribution_period_code",
                label: "دوره توزیع",
                kind: "choice",
                prompt: "کدام دوره توزیع؟",
                options: () =>
                    loadDropdown("/dropdown/distribution-periods", "distribution_period_code", "distribution_period"),
            },
            {
                key: "distribution_stage_code",
                label: "مرحله توزیع",
                kind: "choice",
                prompt: "کدام مرحله توزیع؟",
                // مراحل به دوره وابسته‌اند؛ بدون distribution_period_code
                // سرور فهرست خالی برمی‌گرداند.
                options: (values) =>
                    loadDropdown(
                        "/dropdown/distribution-stages",
                        "distribution_stage_code",
                        "distribution_stage",
                        { distribution_period_code: values.distribution_period_code }
                    ),
            },
        ],
        run: async (values) => {
            const res = await Api.get<any>("/monitoring/reports/flour-distribution-period", {
                params: {
                    distribution_period_code: values.distribution_period_code,
                    distribution_stage_code: values.distribution_stage_code,
                    voucher_to_discharge_days: Number(values.voucher_days) || 30,
                    discharge_to_inspection_days: Number(values.inspection_days) || 30,
                },
            });
            const rows = res.data?.rows || [];
            return {
                title: "گزارش دهه",
                columns: REPORT_COLUMNS,
                rows,
                total: rows.length,
                link: "/ReportsDecadePage",
                note:
                    "دوره: " + values.distribution_period_code +
                    " · مرحله: " + values.distribution_stage_code,
                spoken:
                    rows.length === 0
                        ? "برای این دوره و مرحله گزارشی نیست."
                        : "گزارش دهه با " + rows.length + " ردیف آماده است.",
            };
        },
    },

    // ---------- گزارش تخلفات ----------
    {
        id: "violations",
        title: "گزارش تخلفات",
        roles: [0, 1, 2, 5, 6],
        link: "/ViolationsReport",
        match: [
            ["تخلف", "تخلفات", "خارج از شبکه", "کم پزی", "کم‌پزی"],
            ["گزارش", "لیست", "بده", "بدم", "نشون", "اطلاعات", "چند", "ببین", "بگو"],
        ],
        strip: ["گزارش تخلفات", "تخلفات", "تخلف", "گزارش", "لیست", "بده", "بدم", "رو", "را", "نشونم", "نشون"],
        filterable: true,
        dateField: () => "discharge_date_jalali",
        slots: [
            searchSlot("نام نانوا یا شهر را بگویید. برای دیدن همه بگویید «همه»."),
            optionalRangeSlot,
        ],
        fetchAll: (values) =>
            scanPages(
                "/monitoring/violation-reports/",
                values.search ? { search: values.search } : {},
                MAX_EXPORT_PAGES
            ),
        run: async (values) => {
            const search = String(values.search || "").trim();
            const range = values.range ? unpackRange(values.range) : undefined;
            const params: Record<string, any> = {};
            if (search) params.search = search;

            const found = await listQuery(
                "/monitoring/violation-reports/",
                params,
                range,
                "discharge_date_jalali",
                sizeOf(values)
            );
            return {
                title: "گزارش تخلفات" + (range ? " — " + range.label : ""),
                columns: VIOLATION_COLUMNS,
                rows: found.rows,
                total: found.total,
                link: "/ViolationsReport",
                note: found.note,
                spoken: listSummary("گزارش تخلفات", found.rows.length, found.total, search),
            };
        },
    },

    // ---------- سهمیه خبازی‌ها ----------
    {
        id: "quota",
        title: "سهمیه خبازی‌ها",
        roles: [0, 1, 2, 5, 6],
        link: "/BakerQuota",
        // یک گروه، نه دو: «سهمیه جهانگیر» باید کار کند. واژه‌ی
        // «سهمیه» آن‌قدر مخصوص این صفحه هست که به تنهایی کافی باشد؛
        // با شرط دومِ قبلی، پرسیدن سهمیه‌ی یک نفر با نامش از دست
        // می‌رفت و جمله به پاسخ عمومی می‌افتاد.
        match: [["سهمیه", "سهميه", "کوتا"]],
        strip: [
            "سهمیه خبازی ها", "سهمیه خبازی", "سهمیه", "سهميه", "خبازی", "نانوا",
            "بده", "بدم", "رو", "را", "چند کیسه", "چه قدر", "چقدر", "چنده", "چند",
            "چقدره", "مقدار", "میزان", "کیسه ها", "کیسه", "هر", "بود", "بوده", "هست", "است",
            "میشه", "می شه", "دوره", "ماه", "ماهانه", "ماهیانه", "مقایسه", "روند",
            "افزایش", "کاهش", "تغییر", "کم", "زیاد", "توضیح", "یا", "مجموع",
            // نام ماه‌ها جداگانه از جمله خوانده می‌شوند (monthNameIn)،
            // پس نباید داخل عبارت جست‌وجو بمانند و نام را خراب کنند.
            "فروردین", "اردیبهشت", "خرداد", "تیر", "مرداد", "شهریور",
            "مهر", "آبان", "آذر", "دی", "بهمن", "اسفند",
        ],
        filterable: true,
        slots: [
            searchSlot("نام یا کد خبازی را بگویید. اگر فهرست همه‌ی سهمیه‌ها را می‌خواهید، بگویید «همه»."),
        ],
        fetchAll: (values) =>
            scanPages(
                "/monitoring/bakery-quotas/",
                values.search ? { search: values.search } : {},
                MAX_EXPORT_PAGES
            ),
        run: async (values) => {
            const search = String(values.search || "").trim();
            const sentence = normalize(String(values.sentence || ""));

            // سؤالِ کلیِ مقدار سهمیه بدون نام یا کد، نباید «کیسه» را
            // به‌عنوان نام خبازی جست‌وجو کند یا فهرست خامی برگرداند.
            // نام را می‌پرسیم تا پاسخ ماه‌به‌ماه و روند تغییر دقیق باشد.
            if (
                !search &&
                /چند\s*کیسه|چقدر|چنده|مقدار|میزان/.test(sentence)
            ) {
                const text =
                    "سهمیه‌ی کدوم خبازی رو می‌خواید؟ نام یا کدش رو بگید تا سهمیه‌ی ماه‌به‌ماه و تغییراتش رو بگم.";
                return {
                    title: "سهمیه خبازی‌ها",
                    columns: QUOTA_COLUMNS,
                    rows: [],
                    total: 0,
                    link: "/BakerQuota",
                    spoken: text,
                };
            }

            // نام یک خبازی آمده → پاسخ گفتگویی ماه‌به‌ماه، نه یک جدول خام.
            // «سهمیه‌ی جهانگیر چقدره؟» یعنی همه‌ی دوره‌ها را با هم بگو و
            // بگو کم شده یا زیاد.
            if (search) return describeQuotaFor(search, String(values.sentence || ""));

            const params: Record<string, any> = { page: 1, size: sizeOf(values) };
            const found = await getList<any>("/monitoring/bakery-quotas/", params);
            const sum = found.items.reduce((a: number, r: any) => a + Number(r.quota || 0), 0);
            return {
                title: "سهمیه خبازی‌ها",
                columns: QUOTA_COLUMNS,
                rows: found.items,
                total: found.total,
                link: "/BakerQuota",
                note: found.items.length ? "مجموع سهمیه رکوردهای نشان‌داده‌شده: " + sum : undefined,
                spoken: listSummary("سهمیه خبازی‌ها", found.items.length, found.total, ""),
            };
        },
    },

    // ---------- وظایف راننده ----------
    {
        id: "driver-tasks",
        title: "وظایف راننده",
        roles: [0, 1, 4, 5, 6, 7],
        link: "/DriverTasks",
        match: [
            ["وظایف راننده", "وظیفه راننده", "راننده", "تخلیه"],
            ["وظایف", "وظیفه", "کار", "لیست", "بده", "بدم", "نشون", "چند", "تخلیه", "بگو"],
        ],
        strip: ["وظایف راننده", "وظیفه راننده", "وظایف", "وظیفه", "راننده", "لیست", "بده", "بدم", "رو", "را", "نشونم", "نشون"]
            .concat(STATUS_WORDS),
        filterable: true,
        hasStatusTab: true,
        // رکورد انجام‌نشده هنوز تاریخ تخلیه ندارد؛ تاریخ معنادارش
        // تاریخ حواله است.
        dateField: (values) =>
            values.status === "done" ? "discharge_date_jalali" : "voucher_date_jalali",
        slots: [
            statusSlot(),
            searchSlot("نام نانوا، کد خبازی یا نام راننده را بگویید. برای دیدن همه بگویید «همه»."),
            optionalRangeSlot,
        ],
        fetchAll: (values) =>
            scanPages(
                "/monitoring/tasks/driver",
                {
                    status: values.status || "not_done",
                    ...(values.search ? { search: values.search } : {}),
                },
                MAX_EXPORT_PAGES
            ),
        run: async (values) => {
            const search = String(values.search || "").trim();
            const status = values.status || "not_done";
            const range = values.range ? unpackRange(values.range) : undefined;
            const params: Record<string, any> = { status };
            if (search) params.search = search;

            const found = await listQuery(
                "/monitoring/tasks/driver",
                params,
                range,
                status === "done" ? "discharge_date_jalali" : "voucher_date_jalali",
                sizeOf(values)
            );
            const statusLabel = status === "done" ? "انجام‌شده" : "انجام‌نشده";
            return {
                title: "وظایف راننده — " + statusLabel + (range ? " — " + range.label : ""),
                columns: DRIVER_TASK_COLUMNS,
                rows: found.rows,
                total: found.total,
                link: "/DriverTasks",
                note: found.note,
                spoken: listSummary("وظایف راننده " + statusLabel, found.rows.length, found.total, search),
            };
        },
    },

    // ---------- وظایف بازرس ----------
    {
        id: "inspector-tasks",
        title: "وظایف بازرس شهرستان",
        roles: [0, 1, 2, 5, 6],
        link: "/InspectorTasks",
        match: [
            ["وظایف بازرس", "بازرس", "بازرسی"],
            ["وظایف", "وظیفه", "کار", "لیست", "بده", "بدم", "نشون", "چند", "بازرسی", "بگو"],
        ],
        strip: ["وظایف بازرس شهرستان", "وظایف بازرس", "وظایف", "وظیفه", "بازرس شهرستان", "بازرس", "لیست", "بده", "بدم", "رو", "را"]
            .concat(STATUS_WORDS),
        filterable: true,
        hasStatusTab: true,
        dateField: (values) =>
            values.status === "done" ? "inspection_date_jalali" : "discharge_date_jalali",
        slots: [
            statusSlot(),
            searchSlot("نام نانوا، کد خبازی یا شهر را بگویید. برای دیدن همه بگویید «همه»."),
            optionalRangeSlot,
        ],
        fetchAll: (values) =>
            scanPages(
                "/monitoring/tasks/inspector",
                {
                    status: values.status || "not_done",
                    ...(values.search ? { search: values.search } : {}),
                },
                MAX_EXPORT_PAGES
            ),
        run: async (values) => {
            const search = String(values.search || "").trim();
            const status = values.status || "not_done";
            const range = values.range ? unpackRange(values.range) : undefined;
            const params: Record<string, any> = { status };
            if (search) params.search = search;

            const found = await listQuery(
                "/monitoring/tasks/inspector",
                params,
                range,
                status === "done" ? "inspection_date_jalali" : "discharge_date_jalali",
                sizeOf(values)
            );
            const statusLabel = status === "done" ? "انجام‌شده" : "انجام‌نشده";
            return {
                title: "وظایف بازرس شهرستان — " + statusLabel + (range ? " — " + range.label : ""),
                columns: INSPECTOR_TASK_COLUMNS,
                rows: found.rows,
                total: found.total,
                link: "/InspectorTasks",
                note: found.note,
                spoken: listSummary("وظایف بازرس " + statusLabel, found.rows.length, found.total, search),
            };
        },
    },

    // ---------- وظایف ناظر هوش مصنوعی ----------
    {
        id: "ai-tasks",
        title: "وظایف ناظر هوش مصنوعی",
        roles: [0, 1, 3, 5, 6],
        link: "/AITasks",
        match: [
            ["ناظر هوش مصنوعی", "هوش مصنوعی", "شمارش خودکار", "ناظر هوش"],
            ["وظایف", "وظیفه", "لیست", "بده", "بدم", "نشون", "چند", "بگو", "کار"],
        ],
        strip: ["وظایف ناظر هوش مصنوعی", "ناظر هوش مصنوعی", "هوش مصنوعی", "وظایف", "وظیفه", "لیست", "بده", "بدم", "رو", "را"]
            .concat(STATUS_WORDS),
        filterable: true,
        hasStatusTab: true,
        dateField: () => "created_at_jalali",
        slots: [
            statusSlot(),
            searchSlot("نام نانوا را بگویید. برای دیدن همه بگویید «همه»."),
            optionalRangeSlot,
        ],
        fetchAll: (values) =>
            scanPages(
                "/monitoring/tasks/ai-observer",
                {
                    status: values.status || "not_done",
                    ...(values.search ? { search: values.search } : {}),
                },
                MAX_EXPORT_PAGES
            ),
        run: async (values) => {
            const search = String(values.search || "").trim();
            const status = values.status || "not_done";
            const range = values.range ? unpackRange(values.range) : undefined;
            const params: Record<string, any> = { status };
            if (search) params.search = search;

            const found = await listQuery(
                "/monitoring/tasks/ai-observer",
                params,
                range,
                "created_at_jalali",
                sizeOf(values)
            );
            const statusLabel = status === "done" ? "انجام‌شده" : "انجام‌نشده";
            return {
                title: "وظایف ناظر هوش مصنوعی — " + statusLabel + (range ? " — " + range.label : ""),
                columns: AI_TASK_COLUMNS,
                rows: found.rows,
                total: found.total,
                link: "/AITasks",
                note: found.note,
                spoken: listSummary("وظایف ناظر هوش مصنوعی " + statusLabel, found.rows.length, found.total, search),
            };
        },
    },

    // ---------- کاربران ----------
    {
        id: "users",
        title: "کاربران سامانه",
        roles: [0, 1, 5, 6],
        link: "/manageUser",
        match: [
            ["کاربر", "کاربران", "کاربرها", "یوزر"],
            ["لیست", "اطلاعات", "بده", "بدم", "نشون", "چند", "مدیریت", "بگو", "جستجو"],
        ],
        strip: ["مدیریت کاربران", "کاربران", "کاربرها", "کاربر", "لیست", "اطلاعات", "بده", "بدم", "رو", "را", "نشونم", "نشون"],
        filterable: true,
        slots: [
            searchSlot("نام، نام کاربری یا شماره تلفن کاربر را بگویید. برای دیدن همه بگویید «همه»."),
        ],
        fetchAll: (values) =>
            scanPages(
                "/users/",
                {
                    sort_by: "id",
                    sort_order: "desc",
                    ...(values.search ? { search: values.search } : {}),
                },
                MAX_EXPORT_PAGES
            ),
        run: async (values) => {
            const search = String(values.search || "").trim();
            const params: Record<string, any> = {
                page: 1,
                size: sizeOf(values),
                sort_by: "id",
                sort_order: "desc",
            };
            if (search) params.search = search;
            const found = await getList<any>("/users/", params);
            return {
                title: "کاربران سامانه",
                columns: USER_COLUMNS,
                rows: found.items,
                total: found.total,
                link: "/manageUser",
                spoken: listSummary("کاربران", found.items.length, found.total, search),
            };
        },
    },
];

export const visibleDatasets = (role: number | null): Dataset[] =>
    DATASETS.filter((d) => role !== null && d.roles.indexOf(role) !== -1);

export const datasetById = (id: string): Dataset | undefined =>
    DATASETS.filter((d) => d.id === id)[0];

/** برای اسلات تاریخ: جمله را می‌خواند و اگر تاریخی داشت، مقدار را می‌سازد. */
export const readSlotValue = (slot: Slot, sentence: string): string | null => {
    const n = normalize(sentence);

    if (slot.kind === "daterange") {
        const range = parseDateRange(sentence);
        return range ? packRange(range) : null;
    }

    if (slot.kind === "choice") {
        // وضعیت وظایف، تنها choice ای است که از کلمه هم قابل تشخیص است
        if (slot.key === "status") {
            if (/انجام نشده|نشده|باز|مانده|معوق/.test(n)) return "not_done";
            if (/انجام شده|شده|تمام|بسته|کامل/.test(n)) return "done";
        }
        return null;
    }

    // متن آزاد: «همه» یعنی بدون فیلتر
    if (/^(همه|همش|هم?ه چیز|مهم نیست|فرقی نمیکنه|بیخیال|رد کن|همگی|کل)$/.test(n)) return "";
    return n ? sentence.trim() : null;
};
